/**
 * SPA shell.
 *
 * Wires the AuthProvider, the React Router routes, and the top navigation.
 * Route guards via {@link RequireGroup} hide pages from operators that
 * lack the required Cognito groups (Requirement 8.4). The API server
 * still enforces the same rules; the guard is purely a UX hint.
 *
 * Validates: Requirements 8.1, 8.3, 8.4
 */

import { useEffect, useState } from 'react';
import { BrowserRouter, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { hasAnyGroup } from './auth/cognito';
import { RequireGroup } from './auth/RequireGroup';
import { AdminPage } from './admin/AdminPage';
import { BatchListPage } from './batches/BatchListPage';
import { BatchReviewPage } from './batches/BatchReviewPage';
import { BatchIdentificationWorkbenchPage } from './batches/BatchIdentificationWorkbenchPage';
import { BatchImageReviewPage } from './batches/BatchImageReviewPage';
import { buildInfo, shortRevision } from './build-info';
import { CatalogDetailPage } from './catalog/CatalogDetailPage';
import { loadConfig } from './config';
import { DashboardPage } from './dashboard/DashboardPage';
import { WallboardPage } from './dashboard/WallboardPage';
import {
  EnvironmentBadge,
  EnvironmentBanner,
  EnvironmentDetails,
  EnvironmentGuard,
  isDevelopmentEnvironment,
} from './environment';
import { KitDetailPage } from './kits/KitDetailPage';
import { KittingPage } from './kits/KittingPage';
import { HelpAssistant } from './help/HelpAssistant';
import { MarketplacePage } from './marketplace/MarketplacePage';
import { ConsignmentPage } from './consignment/ConsignmentPage';
import { PrivacyPolicyPage, isPrivacyPolicyPath } from './privacy/PrivacyPolicyPage';
import { StoragePage } from './storage/StoragePage';
import { StorageEntityDetailPage } from './storage/StorageEntityDetailPage';
import {
  isFullWidthWorkbenchPath,
  isImageOnlyReviewPath,
  isWallboardPath,
} from './workbench-layout';

/** Top-level component mounted by `index.tsx`. */
export function App(): JSX.Element {
  if (isPrivacyPolicyPath(window.location.pathname)) {
    return <PrivacyPolicyPage />;
  }

  const config = loadConfig();
  // BrowserRouter wraps AuthProvider so the auth bootstrap can use
  // useNavigate() to land the operator on `/dashboard` after the
  // OAuth code exchange instead of leaving them stuck on `/callback`
  // (which has no route).
  return (
    <EnvironmentGuard config={config} buildRevision={shortRevision(buildInfo.revision)}>
      <BrowserRouter>
        <AuthProvider config={config.cognito}>
          <AppShell />
        </AuthProvider>
      </BrowserRouter>
    </EnvironmentGuard>
  );
}

function AppShell(): JSX.Element {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const auth = useAuth();
  const environment = loadConfig().environment;
  const wallboard = isWallboardPath(location.pathname, location.search);

  // Close the mobile drawer whenever the route changes (a nav link was tapped).
  // Include the query string so switching between Inventory and Find (same
  // pathname, different `?section=`) also closes the drawer.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, location.search]);

  if (auth.loading) {
    return <UnauthenticatedShell status="Checking sign-in..." />;
  }

  if (auth.claims === null) {
    return <UnauthenticatedShell error={auth.error} />;
  }

  return (
    <div
      className="tcvs-shell"
      data-nav-open={navOpen ? 'true' : 'false'}
      data-environment={environment.stage}
      data-dashboard-wallboard={wallboard ? 'true' : 'false'}
    >
      {wallboard ? null : <Header navOpen={navOpen} onToggleNav={() => setNavOpen((v) => !v)} />}
      {wallboard ? null : (
        <div className="tcvs-nav-backdrop" onClick={() => setNavOpen(false)} aria-hidden="true" />
      )}
      <main
        className={[
          'tcvs-main',
          isFullWidthWorkbenchPath(location.pathname, location.search) ? 'tcvs-main-workbench' : '',
          isImageOnlyReviewPath(location.pathname) ? 'tcvs-main-image-review' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/dashboard"
            element={
              <RequireGroup anyOf={['reviewer', 'admin']}>
                <DashboardPage />
              </RequireGroup>
            }
          />
          <Route
            path="/wallboard"
            element={
              <RequireGroup anyOf={['admin']}>
                <WallboardPage />
              </RequireGroup>
            }
          />
          {/* Intake (manual card capture) moved into the Inventory page's
              "Set up" group as "Manual Capture". Redirect the old route so
              existing bookmarks land on that section. */}
          <Route
            path="/intake"
            element={<Navigate to="/storage?section=Manual%20Capture" replace />}
          />
          <Route path="/catalog" element={<Navigate to="/storage?section=Cards" replace />} />
          <Route
            path="/catalog/:id"
            element={
              <RequireGroup anyOf={['reviewer', 'admin']}>
                <CatalogDetailPage />
              </RequireGroup>
            }
          />
          <Route
            path="/batches"
            element={
              <RequireGroup anyOf={['reviewer', 'admin']}>
                <BatchListPage />
              </RequireGroup>
            }
          />
          <Route
            path="/batches/:id"
            element={
              <RequireGroup anyOf={['reviewer', 'admin']}>
                <BatchReviewPage />
              </RequireGroup>
            }
          />
          <Route
            path="/batches/:id/identify"
            element={
              <RequireGroup anyOf={['reviewer', 'admin']}>
                <BatchIdentificationWorkbenchPage />
              </RequireGroup>
            }
          />
          <Route
            path="/batches/:id/images"
            element={
              <RequireGroup anyOf={['reviewer', 'admin']}>
                <BatchImageReviewPage />
              </RequireGroup>
            }
          />
          <Route
            path="/storage"
            element={
              <RequireGroup anyOf={['reviewer', 'admin']}>
                <StoragePage />
              </RequireGroup>
            }
          />
          <Route
            path="/storage/:type/:id"
            element={
              <RequireGroup anyOf={['reviewer', 'admin']}>
                <StorageEntityDetailPage />
              </RequireGroup>
            }
          />
          <Route path="/kits" element={<Navigate to="/kitting" replace />} />
          <Route
            path="/kits/:kitId"
            element={
              <RequireGroup anyOf={['reviewer', 'admin']}>
                <KitDetailPage />
              </RequireGroup>
            }
          />
          <Route
            path="/kitting"
            element={
              <RequireGroup anyOf={['reviewer', 'admin']}>
                <KittingPage />
              </RequireGroup>
            }
          />
          <Route
            path="/kitting/templates"
            element={
              <RequireGroup anyOf={['reviewer', 'admin']}>
                <KittingPage />
              </RequireGroup>
            }
          />
          <Route
            path="/sales"
            element={
              <RequireGroup anyOf={['admin']}>
                <MarketplacePage />
              </RequireGroup>
            }
          />
          <Route
            path="/sales/returns"
            element={
              <RequireGroup anyOf={['reviewer', 'admin']}>
                <MarketplacePage />
              </RequireGroup>
            }
          />
          <Route
            path="/sales/templates"
            element={
              <RequireGroup anyOf={['reviewer', 'admin']}>
                <MarketplacePage />
              </RequireGroup>
            }
          />
          <Route
            path="/sales/reconciliation"
            element={
              <RequireGroup anyOf={['admin']}>
                <MarketplacePage />
              </RequireGroup>
            }
          />
          <Route
            path="/sales/ebay"
            element={
              <RequireGroup anyOf={['admin']}>
                <MarketplacePage />
              </RequireGroup>
            }
          />
          <Route
            path="/sales/ready-to-list"
            element={
              <RequireGroup anyOf={['admin']}>
                <MarketplacePage />
              </RequireGroup>
            }
          />
          <Route
            path="/consignment"
            element={
              <RequireGroup anyOf={['admin']}>
                <ConsignmentPage />
              </RequireGroup>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireGroup anyOf={['admin']}>
                <AdminPage />
              </RequireGroup>
            }
          />
          <Route
            path="/admin/devices"
            element={
              <RequireGroup anyOf={['admin']}>
                <AdminPage />
              </RequireGroup>
            }
          />
          <Route
            path="/admin/checklists"
            element={
              <RequireGroup anyOf={['admin']}>
                <AdminPage />
              </RequireGroup>
            }
          />
          <Route
            path="/admin/help-insights"
            element={
              <RequireGroup anyOf={['admin']}>
                <AdminPage />
              </RequireGroup>
            }
          />
          <Route
            path="/admin/operations"
            element={
              <RequireGroup anyOf={['admin']}>
                <AdminPage />
              </RequireGroup>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </div>
  );
}

function UnauthenticatedShell({
  error,
  status,
}: {
  error?: string | null;
  status?: string;
}): JSX.Element {
  const { signIn } = useAuth();
  const config = loadConfig().cognito;
  const provider = config.preferredIdentityProvider;
  const primaryLabel = 'Sign In';
  const environment = loadConfig().environment;

  return (
    <main className="tcvs-auth-main" data-environment={environment.stage}>
      <EnvironmentBanner environment={environment} />
      <div className="tcvs-vault-door" aria-hidden="true">
        <span className="tcvs-vault-bar tcvs-vault-bar-horizontal" />
        <span className="tcvs-vault-bar tcvs-vault-bar-diagonal-a" />
        <span className="tcvs-vault-bar tcvs-vault-bar-diagonal-b" />
        <span className="tcvs-vault-hinge tcvs-vault-hinge-top" />
        <span className="tcvs-vault-hinge tcvs-vault-hinge-bottom" />
        <span className="tcvs-vault-ring" />
        <span className="tcvs-vault-spoke tcvs-vault-spoke-a" />
        <span className="tcvs-vault-spoke tcvs-vault-spoke-b" />
        <span className="tcvs-vault-spoke tcvs-vault-spoke-c" />
        <span className="tcvs-vault-handle">
          <span className="tcvs-vault-handle-arm tcvs-vault-handle-arm-a" />
          <span className="tcvs-vault-handle-arm tcvs-vault-handle-arm-b" />
          <span className="tcvs-vault-handle-arm tcvs-vault-handle-arm-c" />
          <span className="tcvs-vault-handle-arm tcvs-vault-handle-arm-d" />
        </span>
      </div>
      <section className="tcvs-auth-panel" aria-labelledby="tcvs-auth-heading">
        <div className="tcvs-auth-brand" aria-hidden="true">
          <span className="tcvs-brand-mark" />
          <span>TCVS Vault</span>
          <EnvironmentBadge environment={environment} />
        </div>
        <h1 id="tcvs-auth-heading">Single Sign-On</h1>

        {status !== undefined ? <p>{status}</p> : null}
        {error !== null && error !== undefined ? (
          <p className="tcvs-auth-error" role="alert">
            Sign-in failed: {error}
          </p>
        ) : null}

        <button
          type="button"
          className="tcvs-auth-primary"
          disabled={status !== undefined}
          onClick={() => void signIn(provider)}
        >
          {primaryLabel}
        </button>

        <details className="tcvs-breakglass">
          <summary>Alt Login</summary>
          <p className="tcvs-muted">Use when directed.</p>
          <button
            type="button"
            className="tcvs-btn-secondary"
            disabled={status !== undefined}
            onClick={() => void signIn('COGNITO')}
          >
            Sign In
          </button>
        </details>
        <BuildRevision />
        <a className="tcvs-auth-privacy-link" href="/privacy">
          Privacy
        </a>
      </section>
    </main>
  );
}

/** Apply the active-link class when a nav route is current. */
function navClass({ isActive }: { isActive: boolean }): string {
  return isActive ? 'tcvs-nav-active' : '';
}

function Header({
  navOpen,
  onToggleNav,
}: {
  navOpen: boolean;
  onToggleNav: () => void;
}): JSX.Element {
  const { claims, signIn, signOut, loading } = useAuth();
  const isAdmin = claims !== null && hasAnyGroup(claims, ['admin']);
  // Inventory and Find both render StoragePage; Find deep-links to its
  // `?section=Find` view. NavLink's built-in isActive ignores the query string,
  // so compute active state manually to highlight the two items exclusively.
  const location = useLocation();
  const inStorage = location.pathname === '/storage' || location.pathname.startsWith('/storage/');
  const onFind = inStorage && new URLSearchParams(location.search).get('section') === 'Find';
  const inventoryActive = inStorage && !onFind;
  const kittingActive =
    location.pathname === '/kitting' ||
    location.pathname.startsWith('/kitting/') ||
    location.pathname === '/kits' ||
    location.pathname.startsWith('/kits/');
  const adminActive = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
  const environment = loadConfig().environment;
  const showEnvironmentBanner = isDevelopmentEnvironment(environment);
  return (
    <header
      className={showEnvironmentBanner ? 'tcvs-topbar tcvs-topbar-with-environment' : 'tcvs-topbar'}
    >
      <button
        type="button"
        className="tcvs-hamburger"
        aria-label={navOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={navOpen}
        onClick={onToggleNav}
      >
        {navOpen ? '✕' : '☰'}
      </button>
      <span className="tcvs-brand">
        <span className="tcvs-brand-mark" aria-hidden="true" />
        TCVS Vault
        <EnvironmentBadge environment={environment} />
      </span>
      <nav className="tcvs-nav">
        {/* Overview */}
        <NavLink to="/dashboard" className={navClass}>
          Dashboard
        </NavLink>
        <span className="tcvs-nav-sep" aria-hidden="true" />
        {/* Card lifecycle: confirm → record → place. (Manual card capture
            lives under Inventory › Set up › Manual Capture.) */}
        <NavLink to="/batches" className={navClass}>
          Reviews
        </NavLink>
        <NavLink to="/storage" className={() => (inventoryActive ? 'tcvs-nav-active' : '')}>
          Inventory
        </NavLink>
        <NavLink to="/storage?section=Find" className={() => (onFind ? 'tcvs-nav-active' : '')}>
          Find
        </NavLink>
        <NavLink to="/kitting" className={() => (kittingActive ? 'tcvs-nav-active' : '')}>
          Kitting
        </NavLink>
        {claims !== null && hasAnyGroup(claims, ['admin', 'reviewer']) ? (
          <NavLink to={isAdmin ? '/sales' : '/sales/returns'} className={navClass}>
            Sales
          </NavLink>
        ) : null}
        {isAdmin ? (
          <NavLink to="/consignment" className={navClass}>
            Consignment
          </NavLink>
        ) : null}
        {isAdmin ? (
          <>
            <span className="tcvs-nav-sep" aria-hidden="true" />
            <NavLink to="/admin" className={() => (adminActive ? 'tcvs-nav-active' : '')}>
              Admin
            </NavLink>
          </>
        ) : null}
      </nav>
      <div className="tcvs-userbox">
        <EnvironmentDetails
          environment={environment}
          buildRevision={shortRevision(buildInfo.revision)}
        />
        <BuildRevision />
        <HelpAssistant />
        {loading ? (
          <span>…</span>
        ) : claims === null ? (
          <button type="button" onClick={() => void signIn()}>
            Sign in
          </button>
        ) : (
          <>
            <span className="tcvs-user-email">{claims.email ?? claims.sub}</span>
            <button type="button" className="tcvs-btn-secondary" onClick={signOut}>
              Sign out
            </button>
          </>
        )}
      </div>
      <EnvironmentBanner environment={environment} />
    </header>
  );
}

function BuildRevision(): JSX.Element {
  const title = `Version ${buildInfo.version}\nBuild ${buildInfo.revision}\nBuilt ${buildInfo.builtAt}`;
  return (
    <span className="tcvs-build-revision" title={title} aria-label={title}>
      v{buildInfo.version} · build {shortRevision(buildInfo.revision)}
    </span>
  );
}

function NotFound(): JSX.Element {
  return <p>Not found.</p>;
}
